"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect } from "react";
import { useInView } from "@/hooks/useInView";
import { useOptimizedVideo } from "@/hooks/useOptimizedVideo";
import { useHlsPlayer } from "@/hooks/useHlsPlayer";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { PlayIcon } from "@heroicons/react/24/solid";
import { InlineMediaActions } from "./MediaActionToolbar";
import { useMediaActions } from "./useMediaActions";
interface Props {
  readonly src: string;
  readonly showControls?: boolean | undefined;
}
const MediaDisplayVideo: React.FC<Props> = t0 => {
  const $ = _c(51);
  const {
    src,
    showControls: t1
  } = t0;
  const showControls = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      threshold: 0.1
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [wrapperRef, inView] = useInView(t2);
  const {
    isApp
  } = useDeviceInfo();
  let t3;
  if ($[1] !== src) {
    t3 = {
      url: src,
      fallbackFileName: "video",
      dialogTitle: "Save video"
    };
    $[1] = src;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    downloadMedia,
    isDownloading,
    openLabel,
    openMedia
  } = useMediaActions(t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      pollInterval: 15000,
      maxRetries: 8,
      preferHls: true,
      exponentialBackoff: false
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    playableUrl,
    isHls
  } = useOptimizedVideo(src, t4);
  const t5 = inView && !isApp;
  let t6;
  if ($[4] !== isHls || $[5] !== playableUrl || $[6] !== src || $[7] !== t5) {
    t6 = {
      src: playableUrl,
      isHls,
      fallbackSrc: src,
      autoPlay: t5
    };
    $[4] = isHls;
    $[5] = playableUrl;
    $[6] = src;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    videoRef,
    isLoading
  } = useHlsPlayer(t6);
  const showNativeControls = showControls && !isApp;
  let t7;
  let t8;
  if ($[9] !== videoRef) {
    t7 = () => {
      const vid = videoRef.current;
      if (!vid) {
        return;
      }
      vid.setAttribute("webkit-playsinline", "true");
      vid.setAttribute("x5-playsinline", "true");
    };
    t8 = [videoRef];
    $[9] = videoRef;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[12] !== inView || $[13] !== isApp || $[14] !== isLoading || $[15] !== videoRef) {
    t9 = () => {
      const vid_0 = videoRef.current;
      if (!vid_0 || isLoading) {
        return;
      }
      if (!inView || isApp) {
        vid_0.pause();
      } else {
        vid_0.play().catch(_temp);
      }
    };
    t10 = [inView, isApp, isLoading, videoRef];
    $[12] = inView;
    $[13] = isApp;
    $[14] = isLoading;
    $[15] = videoRef;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[18] !== isApp || $[19] !== videoRef) {
    t11 = () => {
      if (!isApp) {
        return;
      }
      const pauseWhenFullscreenCloses = () => {
        const vid_1 = videoRef.current;
        if (!vid_1 || document.fullscreenElement === vid_1) {
          return;
        }
        vid_1.pause();
      };
      document.addEventListener("fullscreenchange", pauseWhenFullscreenCloses);
      return () => {
        document.removeEventListener("fullscreenchange", pauseWhenFullscreenCloses);
      };
    };
    t12 = [isApp, videoRef];
    $[18] = isApp;
    $[19] = videoRef;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  useEffect(t11, t12);
  let t13;
  if ($[22] !== showControls || $[23] !== videoRef) {
    t13 = () => {
      if (showControls) {
        return;
      }
      const vid_2 = videoRef.current;
      if (!vid_2) {
        return;
      }
      if (vid_2.paused) {
        vid_2.play().catch(_temp2);
        return;
      }
      vid_2.pause();
    };
    $[22] = showControls;
    $[23] = videoRef;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const handleVideoClick = t13;
  let t14;
  if ($[25] !== videoRef) {
    t14 = () => {
      const vid_3 = videoRef.current;
      if (!vid_3) {
        return;
      }
      vid_3.play().catch(_temp3);
      vid_3.requestFullscreen().catch(_temp4);
    };
    $[25] = videoRef;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const enterFullscreenAndPlay = t14;
  let t15;
  if ($[27] !== enterFullscreenAndPlay || $[28] !== handleVideoClick || $[29] !== isApp || $[30] !== showControls) {
    t15 = () => {
      if (showControls && isApp) {
        enterFullscreenAndPlay();
        return;
      }
      handleVideoClick();
    };
    $[27] = enterFullscreenAndPlay;
    $[28] = handleVideoClick;
    $[29] = isApp;
    $[30] = showControls;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== showNativeControls || $[33] !== t15 || $[34] !== videoRef) {
    t16 = <video ref={videoRef} onClick={t15} className="tw-h-auto tw-max-h-64 tw-w-full tw-rounded-xl tw-object-contain" muted={true} loop={true} controls={showNativeControls} controlsList="noplaybackrate" playsInline={true} preload="auto" />;
    $[32] = showNativeControls;
    $[33] = t15;
    $[34] = videoRef;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== enterFullscreenAndPlay || $[37] !== isApp || $[38] !== showControls) {
    t17 = showControls && isApp && <button type="button" aria-label="Play video" title="Play video" onClick={event => {
      event.stopPropagation();
      enterFullscreenAndPlay();
    }} className="tw-absolute tw-left-1/2 tw-top-1/2 tw-z-20 tw-flex tw-size-16 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-iron-700/75 tw-text-white tw-shadow-lg tw-shadow-black/30 tw-backdrop-blur-sm"><PlayIcon className="tw-ml-1 tw-size-8" aria-hidden="true" /></button>;
    $[36] = enterFullscreenAndPlay;
    $[37] = isApp;
    $[38] = showControls;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== downloadMedia || $[41] !== isDownloading || $[42] !== openLabel || $[43] !== openMedia || $[44] !== showControls) {
    t18 = showControls && <InlineMediaActions variant="video" onDownload={downloadMedia} onOpen={openMedia} openLabel={openLabel} isDownloading={isDownloading} />;
    $[40] = downloadMedia;
    $[41] = isDownloading;
    $[42] = openLabel;
    $[43] = openMedia;
    $[44] = showControls;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t16 || $[47] !== t17 || $[48] !== t18 || $[49] !== wrapperRef) {
    t19 = <div ref={wrapperRef} className="tw-relative tw-max-h-64 tw-min-h-[200px] tw-w-full tw-overflow-hidden tw-rounded-xl tw-bg-black">{t16}{t17}{t18}</div>;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
    $[49] = wrapperRef;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  return t19;
};
export default React.memo(MediaDisplayVideo);
function _temp() {}
function _temp2() {}
function _temp3() {}
function _temp4() {}